/**
 * Accessibility layer for the Judge.me featured review carousel.
 *
 * Judge.me renders its own arrows client-side, after Liquid has run, as bare
 * `<div tabindex="0">` elements: no role, no accessible name, and a click-only
 * handler. So a keyboard user can focus an arrow but cannot operate it, and a
 * screen reader announces nothing when they land on one. Neither is reachable
 * from CSS, and the markup is not ours to author, so both are repaired here
 * once the app has injected it.
 *
 * Fixes WCAG 2.1.1 (Keyboard) and 4.1.2 (Name, Role, Value).
 */

const ARROW_SELECTOR = '.jdgm-carousel__left-arrow, .jdgm-carousel__right-arrow';
const ITEM_SELECTOR = '.jdgm-carousel-item';

class DdJudgemeCarousel extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#enhance();

    /*
     * The app injects the carousel asynchronously and re-renders it on resize,
     * so keep watching rather than enhancing once. Sliding is a transform (an
     * attribute change), so this only wakes on genuine node insertions, and
     * setting attributes below cannot re-trigger a childList observer.
     */
    this.#observer = new MutationObserver(() => this.#enhance());
    this.#observer.observe(this, { childList: true, subtree: true });

    this.addEventListener('keydown', this.#handleKeydown);
  }

  #enhance() {
    this.#labelArrows();
    this.#markDistinctReviews();
  }

  /**
   * Judge.me's featured carousel repeats reviews to fill its slots — this shop
   * renders 15 cards from 10 distinct reviews — so the raw DOM order would feed the
   * same testimonial into the mobile scroller more than once. A repeated testimonial
   * reads as fabricated, which is the opposite of what the section is for, so mark
   * every *distinct* review and let the CSS show only those.
   *
   * Desktop is deliberately left alone: the slider derives its offsets from the
   * item count, so hiding items there would let it slide into empty space. The
   * duplicates are a data problem and belong in the Judge.me dashboard.
   */
  #markDistinctReviews() {
    const items = this.querySelectorAll(ITEM_SELECTOR);
    if (items.length === 0) return;

    const seen = new Set();
    let marked = 0;

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      delete item.dataset.ddReview;

      const name = item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent?.trim() ?? '';
      const body = item.querySelector('.jdgm-carousel-item__review-body')?.textContent?.trim() ?? '';
      const key = `${name}::${body}`;

      if (!body || seen.has(key)) continue;

      seen.add(key);
      item.dataset.ddReview = 'true';
      marked += 1;
    }

    // Tells the CSS the marks are trustworthy; without it, it falls back to nth-child.
    if (marked > 0) this.dataset.ddDeduped = 'true';
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * Gives each arrow a role and an accessible name. Idempotent, because the
   * observer re-runs it whenever Judge.me touches the subtree.
   */
  #labelArrows() {
    for (const arrow of this.querySelectorAll(ARROW_SELECTOR)) {
      if (!(arrow instanceof HTMLElement) || arrow.dataset.ddArrowLabelled) continue;

      const isPrevious = arrow.classList.contains('jdgm-carousel__left-arrow');
      const label = isPrevious ? this.dataset.labelPrevious : this.dataset.labelNext;

      arrow.setAttribute('role', 'button');
      if (label) arrow.setAttribute('aria-label', label);
      arrow.dataset.ddArrowLabelled = 'true';
    }
  }

  /**
   * A native button fires a click on Enter and Space. Judge.me's arrows are
   * divs bound to `click` only, so without this they stay focusable but inert.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const { target } = event;
    const arrow = target instanceof Element ? target.closest(ARROW_SELECTOR) : null;

    if (!(arrow instanceof HTMLElement)) return;

    // Space would otherwise scroll the page.
    event.preventDefault();
    arrow.click();
  };
}

if (!customElements.get('dd-judgeme-carousel')) {
  customElements.define('dd-judgeme-carousel', DdJudgemeCarousel);
}
